import { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';

import { ButtonPrimary, CustomDatePicker, ModalClose } from '../../common/components';
import { FormProvider, useForm } from 'react-hook-form';
import ReactSelect from 'react-select';
import { ScheduledReportModalWrapper } from './ScheduledReport.styles';
import { DateObject } from 'react-multi-date-picker';
import moment from 'moment';
import { REVERSED_DATE_FORMAT_HYPHEN } from '../../common/constants';
import { TagManager } from '../utils/analytics/TagManager';
import { isTpiUser } from '../../helpers/tpiUser';
import { getMetersTwoYearsOld } from '../../common/api/meterReadsApi';
import { useAuthState } from '../../providers/authProvider';
import { getSmartMeterConsentGridData } from '../../common/api/consentApi';

type ScheduledReportModalProps = {
  show: boolean;
  edit: boolean;
  tpiUser: boolean;
  accountReferences: Array<any>;
  emails: Array<Api.IEmailRecipientScheduledReportRequest>;
  requesterEmailAddress: string;
  item: Api.IScheduledReportRequest;
  selectedEmails: Array<any>;
  onSubmit: (isAccepted: boolean, item: any) => void;
  onClose: () => void;
};

type DownloadTypes = {
  value: string;
  label: string;
  description: string;
};

const defaultProps = {
  show: true,
  edit: false,
  item: {},
  requesterEmailAddress: '',
  onSubmit: (isAccepted: boolean, item: any) => console.log(isAccepted),
};

type Inputs = {
  email: string;
  downloadReport: { label: string; value: string };
};

export const ScheduledReportModal = (props: ScheduledReportModalProps) => {
  const authContext = useAuthState();
  const { show, onSubmit } = props;
  const [submitForm, setSubmitForm] = useState(false);

  const methods = useForm<Inputs>({ defaultValues: {} });

  const [selectedAccountReferenceItem, setSelectedAccountReferenceItem] = useState<any>(null);
  const [selectedDownloadReportItem, setSelectedDownloadReportItem] = useState<any>(null);
  const [selectedFormatTypeItem, setSelectedFormatTypeItem] = useState<any>();
  const [selectedFrequencyItem, setSelectedFrequencyItem] = useState<any>();
  const [startMonthPickerValue, setStartMonthPickerValue] = useState<any>(null);
  const [selectedDurationItem, setSelectedDurationItem] = useState<any>(null);
  const [selectedDayOfMonthItem, setSelectedDayOfMonthItem] = useState<any>();
  const [selectedEmails, setSelectedEmails] = useState<Array<any>>([]);
  const [requesterEmailAddress, setRequesterEmailAddress] = useState<any>();
  const [availableDownloadTypes, setAvailableDownloadTypes] = useState<Array<DownloadTypes>>([]);
  const [smartConsentNotSet, setSmartConsentNotSet] = useState<boolean>(false);

  const accountReferences = props.accountReferences
    ? Array.from(props.accountReferences, (accountReference: any) => {
        return {
          value: accountReference.essAccountID.toString(),
          label: accountReference.essAccountID + ' - ' + accountReference.accountName,
        };
      })
    : [];

  const allDownloadTypes = [
    {
      value: 'HHData',
      label: 'HH data',
      description:
        'The report will contain Half-hourly consumption data for all active HH MPANS. Data provided will be for the previous calendar month before the report is available',
    },
    {
      value: 'AMRData',
      label: 'AMR data',
      description:
        'The report will contain Half-hourly consumption data for all active AMR MPANS. Data provided will be for the previous calendar month before the report is available. <p class="pt-2 mb-1">We can only provide data from the following Data Collectors - Callisto, Energy Assets, IMserv & Stark</p>',
    },
    {
      value: 'InvoicePdfs',
      label: "Invoice pdf's",
      description: 'A zipped file containing all invoice and credit notes issued the previous calendar month',
    },
    {
      value: 'InvoiceBackingData',
      label: 'Invoice backing data',
      description: 'A report detailing the invoice/credit note line charges issued the previous calendar month',
    },
    {
      value: 'SmartMeterData',
      label: 'Smart meter data',
      description: `The report will contain Half-hourly consumption data for all active Smart meter MPANS. Data provided will be for the previous calendar month before the report is available`,
    },
  ];

  const hhDataReport = {
    value: 'HHData',
    label: 'HH data',
    description:
      'The report will contain Half-hourly consumption data for all active HH MPANS. Data provided will be for the previous calendar month before the report is available',
  };

  const amrDataReport = {
    value: 'AMRData',
    label: 'AMR data',
    description:
      'The report will contain Half-hourly consumption data for all active AMR MPANS. Data provided will be for the previous calendar month before the report is available. <p class="pt-2 mb-1">We can only provide data from the following Data Collectors - Callisto, Energy Assets, IMserv & Stark</p>',
  };

  const smartDataReport = {
    value: 'SmartMeterData',
    label: 'Smart meter data',
    description: `The report will contain Half-hourly consumption data for all active Smart meter MPANS. Data provided will be for the previous calendar month before the report is available`,
  };

  const availableReports = [
    {
      value: 'InvoicePdfs',
      label: "Invoice pdf's",
      description: 'A zipped file containing all invoice and credit notes issued the previous calendar month',
    },
    {
      value: 'InvoiceBackingData',
      label: 'Invoice backing data',
      description: 'A report detailing the invoice/credit note line charges issued the previous calendar month',
    },
  ];

  const formatTypesDownloadHHData = [
    { value: 'xml', label: 'xml' },
    { value: 'xlsx', label: 'Excel' },
  ];

  const formatTypesDownloadInvoicePdfHData = [{ value: 'pdf', label: 'pdf' }];

  const formatTypesDownloadBackingData = [{ value: 'xlsx', label: 'Excel' }];

  var formatTypes =
    selectedDownloadReportItem?.label === 'HH data' ||
    selectedDownloadReportItem?.label === 'AMR data' ||
    selectedDownloadReportItem?.label === 'Smart meter data'
      ? formatTypesDownloadHHData
      : selectedDownloadReportItem?.label === "Invoice pdf's"
      ? formatTypesDownloadInvoicePdfHData
      : selectedDownloadReportItem?.label === 'Invoice backing data'
      ? formatTypesDownloadBackingData
      : [];

  const frequencies = [{ value: 'Monthly', label: 'Monthly' }];

  const duration = Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;
    return { value: month.toString(), label: month.toString() };
  });

  const daysOfMonth = Array.from({ length: 28 }, (_, i) => {
    const day = i + 1;
    let suffix = 'th';

    if (day === 1 || day === 21) suffix = 'st';
    else if (day === 2 || day === 22) suffix = 'nd';
    else if (day === 3 || day === 23) suffix = 'rd';

    return { value: day.toString(), label: `${day}${suffix}` };
  });

  const filteredDaysOfMonth = () => {
    const selectedMonth = startMonthPickerValue?.month.number - 1;
    const currentMonth = moment().month();
    const currentDay = moment().date();

    if (selectedMonth === currentMonth) {
      daysOfMonth.splice(0, currentDay);
    }

    return daysOfMonth;
  };

  const startDateDate = () => {
    const todaysDate = moment().date();

    if (todaysDate > 28) {
      return moment().add(1, 'months').startOf('month').format(REVERSED_DATE_FORMAT_HYPHEN);
    } else {
      return moment().format(REVERSED_DATE_FORMAT_HYPHEN);
    }
  };

  const endDate = moment().add(11, 'months').startOf('month').format(REVERSED_DATE_FORMAT_HYPHEN);

  const emailOptions = props.emails
    ? Array.from(props.emails, (email: any) => {
        return { value: email.emailUserId.toString(), label: email.email.toString() };
      })
    : [];

  const selectedEmailOptions = selectedEmails
    ? Array.from(selectedEmails, (email: any) => {
        return { value: email.emailUserId.toString(), label: email.email.toString() };
      })
    : [];

  const handleEmailChange = (selectedEmailOptions: any) => {
    let selected: Array<Api.IEmailRecipientScheduledReportRequest> = [];

    selectedEmailOptions.map((item: any) => {
      const selectedEmailDetails: Api.IEmailRecipientScheduledReportRequest = {
        email: item.label,
        emailUserId: item.value,
      };
      selected.push(selectedEmailDetails);
    });

    setSelectedEmails(selected);
  };

  const handleAccountSelection = () => {
    if (props.item.customerAccountReference) {
      setSelectedAccountReferenceItem({
        value: props.item.customerAccountReference,
        label: props.item.customerAccountReference,
      });
    } else {
      if (props.accountReferences.length === 1) {
        setSelectedAccountReferenceItem({
          value: props.accountReferences[0].essAccountID,
          label: props.accountReferences[0].essAccountID + ' - ' + props.accountReferences[0].accountName,
        });
      } else {
        setSelectedAccountReferenceItem(null);
      }
    }
  };

  const handleSubmitGA = () => {
    TagManager.pushData({
      event: 'Create scheduled report',
      userType: isTpiUser() ? 'TPI User' : 'Customer',
      customerAccountReference: selectedAccountReferenceItem?.value,
      downloadReportType: selectedDownloadReportItem?.value,
      fileType: selectedFormatTypeItem?.value,
    });
  };

  useEffect(() => {
    filterReportTypes(selectedAccountReferenceItem?.value);
  }, [selectedAccountReferenceItem]);

  useEffect(() => {
    const selectedFormatTypes = formatTypes.find(item => item.value === props.item.fileType);
    setSelectedFormatTypeItem(selectedFormatTypes ? selectedFormatTypes : null);
  }, [selectedDownloadReportItem]);

  useEffect(() => {
    if (!props.show) return;

    handleAccountSelection();

    setRequesterEmailAddress(props.requesterEmailAddress);

    if (!props.edit) {
      setSelectedDownloadReportItem(null);
      setSelectedFormatTypeItem(null);
      setSelectedFrequencyItem(null);
      setSelectedDayOfMonthItem(null);
      setStartMonthPickerValue(null);
      setSelectedDurationItem(null);
      setSelectedEmails([]);
      return;
    }

    const selectedDownloadReport = allDownloadTypes.find(item => item.value === props.item.reportName);
    setSelectedDownloadReportItem(selectedDownloadReport);

    const selectedFrequency = frequencies.find(item => item.value === props.item.frequency);
    setSelectedFrequencyItem(selectedFrequency);

    const selectedDayOfMonth = daysOfMonth.find(item => parseInt(item.value) === props.item.scheduledDayOfMonth);
    setSelectedDayOfMonthItem(selectedDayOfMonth);

    if (props.item.startDate) {
      const selectedStartMonthValue: DateObject = new DateObject(props.item.startDate);

      setStartMonthPickerValue(selectedStartMonthValue);
    } else {
      setStartMonthPickerValue(null);
    }

    const selectedDuration = duration.find(item => parseInt(item.value) === props.item.duration);
    setSelectedDurationItem(selectedDuration);

    setSelectedEmails(props.selectedEmails);
  }, [props.show]);

  useEffect(() => {
    var valid = false;
    valid =
      selectedAccountReferenceItem != null &&
      selectedDownloadReportItem != null &&
      selectedFormatTypeItem != null &&
      selectedFrequencyItem != null &&
      selectedDurationItem != null &&
      startMonthPickerValue != null;

    if (valid) {
      if (selectedFrequencyItem && selectedFrequencyItem.value === 'Monthly') {
        valid = selectedDayOfMonthItem != null;
      }
    }

    setSubmitForm(valid);
  }, [
    selectedAccountReferenceItem,
    selectedDownloadReportItem,
    selectedFormatTypeItem,
    selectedFrequencyItem,
    startMonthPickerValue,
    selectedDurationItem,
    selectedDayOfMonthItem,
    selectedEmails,
  ]);

  const filterReportTypes = async (accountId: string) => {
    if (accountId == null) return;
    setSmartConsentNotSet(false);
    addHHReportType(accountId);
    addSmartReportType(accountId);
    setAvailableDownloadTypes(availableReports);
  };

  const addHHReportType = async (accountId: string) => {
    const meters = await getMetersTwoYearsOld(process.env.REACT_APP_API_URL, authContext, accountId);

    for (let i = 0; i < meters.length; i++) {
      if (meters[i]['hh-or-nhh'] === 'HH') {
        availableReports.unshift(hhDataReport);
        break;
      }
    }

    for (let i = 0; i < meters.length; i++) {
      if (
        meters[i]['supply-date-removed'] === null &&
        meters[i]['smart-meter'] === true &&
        (meters[i]['meter-type'] === 'NCAMR' ||
          meters[i]['meter-type'] === 'RCAMR' ||
          meters[i]['meter-type'] === 'RCAMY')
      ) {
        availableReports.unshift(amrDataReport);
        break;
      }
    }
  };

  const addSmartReportType = async (accountId: string) => {
    const smartData: Common.IResult<Array<Api.ISmartMeterConsent>> = await getSmartMeterConsentGridData(
      process.env.REACT_APP_API_URL,
      authContext,
      accountId
    );

    if (smartData && smartData.success && smartData.data.length > 0) {
      availableReports.unshift(smartDataReport);

      for (let i = 0; i < smartData.data.length; i++) {
        if (smartData.data[i]['consent'] === 'Not set') {
          setSmartConsentNotSet(true);
          break;
        }
      }
    }
  };

  return (
    <>
      <Modal animation={false} className={'custom-modal'} show={show} size={'lg'} backdrop='static' centered>
        <ScheduledReportModalWrapper>
          <Modal.Header>
            {props.edit ? (
              <Modal.Title>Edit Scheduled Report</Modal.Title>
            ) : (
              <Modal.Title>Add Scheduled Report</Modal.Title>
            )}
            <ModalClose onClick={props.onClose} />
          </Modal.Header>

          <Modal.Body>
            <FormProvider {...methods}>
              <form>
                <div className='d-flex-column'>
                  <div>
                    Create an automated report giving you data on a monthly basis. You will receive an email when the
                    report is available for downloading
                  </div>
                  <div className='my-2'>
                    <ReactSelect
                      autoFocus={false}
                      isClearable={true}
                      blurInputOnSelect={true}
                      placeholder={'Account'}
                      value={selectedAccountReferenceItem}
                      options={accountReferences}
                      isDisabled={props.edit ? true : false}
                      onChange={value => {
                        setSelectedAccountReferenceItem(value);
                        setSelectedDownloadReportItem(null);
                      }}
                    />

                    <ReactSelect
                      className='pt-2'
                      autoFocus={false}
                      isClearable={true}
                      blurInputOnSelect={true}
                      placeholder={'Download report'}
                      value={selectedDownloadReportItem}
                      options={availableDownloadTypes}
                      isDisabled={props.edit || !selectedAccountReferenceItem ? true : false}
                      onChange={value => {
                        setSelectedDownloadReportItem(value);
                        setSelectedFormatTypeItem(null);
                      }}
                    />

                    <div className='pt-2'>
                      {selectedDownloadReportItem && selectedDownloadReportItem?.description && (
                        <span dangerouslySetInnerHTML={{ __html: selectedDownloadReportItem?.description }} />
                      )}
                      {smartConsentNotSet && selectedDownloadReportItem?.value === 'SmartMeterData' && (
                        <p className='pt-2 mb-0'>
                          Some MPAN’s will require consent to be given to enable us to deliver the HH data. Consent
                          status for Smart Meters can be updated{' '}
                          <a className='consentLink' href='/home/energy-usage/smartmeterconsent'>
                            here
                          </a>
                        </p>
                      )}
                    </div>

                    <ReactSelect
                      className='pt-2'
                      autoFocus={false}
                      isClearable={true}
                      blurInputOnSelect={true}
                      placeholder={'Report format'}
                      value={selectedFormatTypeItem}
                      options={formatTypes}
                      isDisabled={selectedDownloadReportItem === null || props.edit ? true : false}
                      onChange={value => {
                        setSelectedFormatTypeItem(value);
                      }}
                    />
                    <ReactSelect
                      className='pt-2'
                      autoFocus={false}
                      isClearable={true}
                      blurInputOnSelect={true}
                      placeholder={'Frequency'}
                      value={selectedFrequencyItem}
                      options={frequencies}
                      isDisabled={props.edit ? true : false}
                      onChange={value => {
                        setSelectedFrequencyItem(value);
                      }}
                    />
                    <p className='pt-2 pl-2 mb-0'>Report start month</p>
                    <CustomDatePicker
                      date={startMonthPickerValue}
                      minDate={startDateDate()}
                      maxDate={endDate}
                      format='MMM YY'
                      showIcon={true}
                      onChange={(date: any) => {
                        setStartMonthPickerValue(date);
                        setSelectedDayOfMonthItem(null);
                      }}
                      onlyMonthPicker={true}
                      hideYear={false}
                    />
                    <p className='pt-2 pl-2 mb-0'>What day of the month would you like to receive the report?</p>
                    <ReactSelect
                      className='pt-2'
                      autoFocus={false}
                      isClearable={true}
                      blurInputOnSelect={true}
                      placeholder={'Report delivery day'}
                      value={selectedDayOfMonthItem}
                      options={filteredDaysOfMonth()}
                      isDisabled={selectedFrequencyItem?.value !== 'Monthly'}
                      onChange={value => {
                        setSelectedDayOfMonthItem(value);
                      }}
                    />
                    <p className='pt-2 pl-2 mb-0'>How many months would you like the report to run?</p>
                    <ReactSelect
                      className='pt-2'
                      autoFocus={false}
                      isClearable={true}
                      blurInputOnSelect={true}
                      placeholder={'Duration'}
                      value={selectedDurationItem}
                      options={duration}
                      onChange={value => {
                        setSelectedDurationItem(value);
                      }}
                    />

                    {props.tpiUser && (
                      <ReactSelect
                        className='pt-2'
                        isMulti={true}
                        autoFocus={false}
                        isClearable={true}
                        blurInputOnSelect={true}
                        placeholder={'Additional Email Addresses'}
                        value={selectedEmailOptions}
                        options={emailOptions}
                        onChange={handleEmailChange}
                      />
                    )}

                    {requesterEmailAddress && (
                      <div className='pt-2 ml-1 pl-2'>Requested By: {requesterEmailAddress}</div>
                    )}

                    <div className='pt-4'>
                      <ButtonPrimary
                        disabled={!submitForm}
                        onClick={event => {
                          event.preventDefault();

                          handleSubmitGA();

                          const startMonth = `${
                            startMonthPickerValue?.year
                          }-${startMonthPickerValue?.month.number.toLocaleString(undefined, {
                            minimumIntegerDigits: 2,
                            useGrouping: false,
                          })}-01`;

                          const item: Api.IScheduledReportRequest = {
                            id: props.item.id,
                            reportName: selectedDownloadReportItem?.value,
                            customerAccountReference: selectedAccountReferenceItem?.value,
                            scheduledDayOfMonth: selectedDayOfMonthItem?.value,
                            fileType: selectedFormatTypeItem?.value,
                            startDate: startMonth,
                            duration: selectedDurationItem?.value,
                            frequency: selectedFrequencyItem?.value,
                            emailRecipientScheduledReportRequests: selectedEmails,
                          };

                          onSubmit(submitForm, item);
                        }}
                        className='mt-2 pt-2 submit-btn'
                        title={props.edit ? 'Update' : 'Create'}
                      ></ButtonPrimary>

                      <ButtonPrimary
                        onClick={event => {
                          event.preventDefault();
                          props.onClose();
                        }}
                        className='ml-2'
                        title={'Cancel'}
                      ></ButtonPrimary>
                    </div>
                  </div>
                </div>
              </form>
            </FormProvider>
          </Modal.Body>
        </ScheduledReportModalWrapper>
      </Modal>
    </>
  );
};

ScheduledReportModal.defaultProps = defaultProps;
